import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowUpRight, Maximize2, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { LngLatBounds, Map as MapLibreMap, Popup } from "maplibre-gl";
import Header from "../../components/Header/Header";
import TitleBand from "../../components/TitleBand/TitleBand";
import ContactForm from "../../components/Forms/ContactForm";
import ChatWidget from "../../components/ChatWidget/ChatWidget";
import { PageSeo } from "../../seo/Seo";
import { MAP_PATH, Office, useOffices } from "./offices";
import "./Mapa.css";

type MapLib = typeof import("maplibre-gl");
type MapContext = { lib: MapLib; map: MapLibreMap; markers: HTMLElement[]; bounds: LngLatBounds };

// Mapa vetorial do OpenFreeMap: gratuito, sem chave e com uso comercial liberado.
const MAP_STYLE = "https://tiles.openfreemap.org/styles/fiord";

// Cores da marca (design-system.css): as camadas do mapa não leem variáveis CSS.
const NAVY_800 = "#1B1C6E";
const NAVY_900 = "#0E0F3D";
const PINK = "#E8335A";
const ON_DARK_2 = "#C9CBF0";
const ON_DARK_3 = "#9A9CD0";
const ON_DARK_4 = "#7F82C2";
const LILAC = "201, 203, 240"; // --cz-on-dark-2 em RGB, para variar a opacidade

// A gota do pino (36px) é girada 45°: a ponta fica ~7,5px abaixo da caixa do marcador. O popup abre acima da gota.
const PIN_TIP = 7.5;
const POPUP_OFFSET = 62;

// Tracejado correndo da matriz para fora (sequência de padrões de traço, como no exemplo oficial do MapLibre).
const DASH_STEPS = [
  [0, 4, 3], [0.5, 4, 2.5], [1, 4, 2], [1.5, 4, 1.5], [2, 4, 1], [2.5, 4, 0.5], [3, 4, 0],
  [0, 0.5, 3, 3.5], [0, 1, 3, 3], [0, 1.5, 3, 2.5], [0, 2, 3, 2], [0, 2.5, 3, 1.5], [0, 3, 3, 1], [0, 3.5, 3, 0.5],
];

const pad = (n: number) => String(n + 1).padStart(2, "0");
const isDesktop = () => window.matchMedia("(min-width: 761px)").matches;
const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// No desktop o painel cobre a esquerda do mapa: enquadramentos e voos descontam essa largura. À direita, folga para
// o rótulo da cidade, que fica ao lado do pino; embaixo, no mobile, para o painel que sobe sobre o mapa.
const panelOffset = (panel: HTMLElement | null) => (isDesktop() && panel ? panel.offsetWidth + 24 : 0);
const framePadding = (panel: HTMLElement | null) => ({
  top: 90,
  right: 150,
  bottom: isDesktop() ? 100 : 120,
  left: panelOffset(panel) + 70,
});

// O pacote é UMD dentro de um package "type": "module": conforme o bundler, a API chega no namespace, no default
// ou só no global window.maplibregl.
function resolveMapLib(mod: unknown): MapLib {
  const m = mod as Partial<MapLib> & { default?: MapLib };
  if (m.Map) return m as MapLib;
  if (m.default?.Map) return m.default;
  return (window as unknown as { maplibregl: MapLib }).maplibregl;
}

// Arco da matriz até cada escritório, em [lng, lat]: curva de Bézier quadrática com o controle deslocado na
// perpendicular (SP → Orlando sai pelo Atlântico).
function arc(a: [number, number], b: [number, number], bend = 0.2) {
  const cx = (a[0] + b[0]) / 2 + (b[1] - a[1]) * bend;
  const cy = (a[1] + b[1]) / 2 - (b[0] - a[0]) * bend;
  const points: [number, number][] = [];
  for (let i = 0; i <= 48; i++) {
    const t = i / 48;
    const u = 1 - t;
    points.push([u * u * a[0] + 2 * u * t * cx + t * t * b[0], u * u * a[1] + 2 * u * t * cy + t * t * b[1]]);
  }
  return points;
}

// Estilo "fiord" puxado para o navy da marca: terra navy-800, água navy-900, vias e limites em lilás translúcido
// e rótulos nos tons de texto sobre escuro.
function paintBrand(map: MapLibreMap) {
  const set = (id: string, prop: string, value: string) => {
    if (map.getLayer(id)) map.setPaintProperty(id, prop, value);
  };
  set("background", "background-color", NAVY_800);
  set("water", "fill-color", NAVY_900);
  set("waterway", "line-color", NAVY_900);
  set("landuse_residential", "fill-color", `rgba(${LILAC}, .04)`);
  set("landcover_wood", "fill-color", `rgba(${LILAC}, .04)`);
  set("park", "fill-color", `rgba(${LILAC}, .07)`);
  set("building", "fill-color", "rgba(14, 15, 61, .55)");
  set("boundary_state", "line-color", `rgba(${LILAC}, .16)`);
  set("boundary_country_z0-4", "line-color", `rgba(${LILAC}, .45)`);
  set("boundary_country_z5-", "line-color", `rgba(${LILAC}, .45)`);
  map.getStyle().layers.forEach(({ id, type }) => {
    if (type === "line" && /^(highway|tunnel)_/.test(id)) set(id, "line-color", `rgba(${LILAC}, .18)`);
    if (type === "symbol") {
      set(id, "text-halo-color", NAVY_900);
      set(id, "text-color", /^place_(city|town|continent)/.test(id) ? ON_DARK_2 : /^place_(country|state)/.test(id) ? ON_DARK_3 : ON_DARK_4);
    }
  });
}

function popupContent(office: Office) {
  const root = document.createElement("div");
  root.className = "cz-map-popup";
  const add = (tag: string, className: string, text: string) => {
    const el = document.createElement(tag);
    if (className) el.className = className;
    el.textContent = text;
    root.appendChild(el);
  };
  add("span", "cz-map-popup__tag", office.tag);
  add("strong", "cz-map-popup__city", office.city);
  add("span", "", office.street);
  add("span", "", office.postal);
  return root;
}

const directionsUrl = (office: Office) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(office.query)}`;

// Onde estamos: o mapa ocupa uma caixa navy (a mesma do bloco de contato) e o painel branco dos escritórios flutua
// por cima. Clicar num escritório voa até ele; ?u=0|1 abre a página já focada (links "Ver no mapa" do rodapé).
export default function Mapa() {
  const { t } = useTranslation();
  const offices = useOffices();
  const location = useLocation();
  const canvasRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const mapRef = useRef<MapContext | null>(null);
  const popupRef = useRef<Popup | null>(null);
  const activeRef = useRef(-1);
  const latest = useRef({ offices, t });
  latest.current = { offices, t };
  const [active, setActive] = useState(-1);
  const [status, setStatus] = useState<"loading" | "ready" | "failed">("loading");

  const focus = useCallback((i: number, fly: boolean) => {
    activeRef.current = i;
    setActive(i);
    popupRef.current?.remove();
    const ctx = mapRef.current;
    if (!ctx) return;

    const { lib, map } = ctx;
    const office = latest.current.offices[i];
    const open = () => {
      if (activeRef.current !== i) return;
      popupRef.current?.remove();
      popupRef.current = new lib.Popup({
        anchor: "bottom",
        offset: POPUP_OFFSET,
        maxWidth: "300px",
        focusAfterOpen: false,
        className: "cz-map-popup-wrap",
      })
        .setLngLat(office.lngLat)
        .setDOMContent(popupContent(office))
        .addTo(map);
    };
    if (!fly) return open();

    // offset: centraliza o escritório na parte visível do mapa (à direita do painel, no desktop)
    const camera = { center: office.lngLat, zoom: 15, offset: [panelOffset(panelRef.current) / 2, 0] as [number, number] };
    if (reducedMotion()) {
      map.easeTo({ ...camera, duration: 0 });
      open();
    } else {
      map.once("moveend", open);
      map.flyTo({ ...camera, duration: 1200 });
    }
  }, []);

  const showAll = useCallback(() => {
    activeRef.current = -1;
    setActive(-1);
    popupRef.current?.remove();
    const ctx = mapRef.current;
    if (ctx) ctx.map.fitBounds(ctx.bounds, { padding: framePadding(panelRef.current), duration: reducedMotion() ? 0 : 1000 });
  }, []);

  // Monta o mapa (biblioteca carregada sob demanda, fora do bundle principal). No pré-render (Chrome automatizado)
  // ele não sobe: o HTML salvo fica só com o painel.
  useEffect(() => {
    const container = canvasRef.current;
    if (!container || navigator.webdriver) return;
    let cancelled = false;
    let map: MapLibreMap | null = null;
    let frame = 0;
    let resizeTimer = 0;
    let observer: ResizeObserver | null = null;

    Promise.all([import("maplibre-gl"), import("maplibre-gl/dist/maplibre-gl.css")])
      .then(([mod]) => {
        if (cancelled) return;
        const lib = resolveMapLib(mod);
        const { offices, t } = latest.current;
        const bounds = offices.reduce(
          (b, office) => b.extend(office.lngLat),
          new lib.LngLatBounds(offices[0].lngLat, offices[0].lngLat)
        );

        const m = new lib.Map({
          container,
          style: MAP_STYLE,
          bounds,
          fitBoundsOptions: { padding: framePadding(panelRef.current) },
          // dentro da página a rolagem do mouse rola a página; zoom só com Ctrl + rolagem (ou dois dedos no celular)
          cooperativeGestures: true,
          dragRotate: false,
          pitchWithRotate: false,
          attributionControl: false,
          locale: {
            "Map.Title": t("map_page.map_label", "Mapa dos escritórios da Calriz"),
            "NavigationControl.ZoomIn": t("map_page.zoom_in", "Aproximar"),
            "NavigationControl.ZoomOut": t("map_page.zoom_out", "Afastar"),
            "Popup.Close": t("map_page.close", "Fechar"),
            "CooperativeGesturesHandler.WindowsHelpText": t("map_page.gesture_windows", "Use Ctrl + rolagem para dar zoom no mapa"),
            "CooperativeGesturesHandler.MacHelpText": t("map_page.gesture_mac", "Use ⌘ + rolagem para dar zoom no mapa"),
            "CooperativeGesturesHandler.MobileHelpText": t("map_page.gesture_mobile", "Use dois dedos para mover o mapa"),
          },
        });
        map = m;
        m.touchZoomRotate.disableRotation();
        // zoom e escala no canto de cima (embaixo à direita fica São Paulo); atribuição embaixo
        m.addControl(new lib.NavigationControl({ showCompass: false }), "top-right");
        m.addControl(new lib.ScaleControl({ unit: "metric" }), "top-right");
        m.addControl(new lib.AttributionControl({ compact: true }), "bottom-right");
        // o estilo cita ícones que não vêm no pacote; um pixel transparente evita o aviso no console
        m.on("styleimagemissing", (e) => {
          if (!m.hasImage(e.id)) m.addImage(e.id, { width: 1, height: 1, data: new Uint8Array(4) });
        });

        // O MapLibre posiciona o elemento externo via transform; a gota (e o zoom do ativo) ficam nos internos.
        const markers = offices.map((office, i) => {
          const el = document.createElement("div");
          el.className = "cz-map-marker";
          el.setAttribute("aria-label", office.city);
          el.innerHTML =
            `<span class="cz-map-marker__pin"><span class="cz-map-pin"><b>${pad(i)}</b></span></span>` +
            `<span class="cz-map-marker__label">${office.city}</span>`;
          el.addEventListener("click", (e) => {
            e.stopPropagation();
            focus(i, false);
          });
          el.addEventListener("keydown", (e) => {
            if (e.key !== "Enter" && e.key !== " ") return;
            e.preventDefault();
            focus(i, false);
          });
          new lib.Marker({ element: el, anchor: "bottom", offset: [0, -PIN_TIP] }).setLngLat(office.lngLat).addTo(m);
          return el;
        });
        mapRef.current = { lib, map: m, markers, bounds };

        m.on("load", () => {
          paintBrand(m);
          const [hq, ...others] = offices;
          m.addSource("arcs", {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: others.map((office) => ({
                type: "Feature",
                properties: {},
                geometry: { type: "LineString", coordinates: arc(hq.lngLat, office.lngLat) },
              })),
            },
          });
          const layout = { "line-cap": "round", "line-join": "round" } as const;
          m.addLayer({ id: "arcs-glow", type: "line", source: "arcs", layout, paint: { "line-color": PINK, "line-width": 9, "line-opacity": 0.18, "line-blur": 2 } });
          m.addLayer({ id: "arcs-dash", type: "line", source: "arcs", layout, paint: { "line-color": PINK, "line-width": 2.5, "line-dasharray": DASH_STEPS[0] } });
          if (!reducedMotion()) {
            let step = -1;
            const flow = (time: number) => {
              const n = Math.floor(time / 60) % DASH_STEPS.length;
              if (n !== step) {
                step = n;
                m.setPaintProperty("arcs-dash", "line-dasharray", DASH_STEPS[n]);
              }
              frame = requestAnimationFrame(flow);
            };
            frame = requestAnimationFrame(flow);
          }
          setStatus("ready");
        });

        observer = new ResizeObserver(() => {
          m.resize();
          clearTimeout(resizeTimer);
          resizeTimer = window.setTimeout(() => {
            if (activeRef.current < 0) m.fitBounds(bounds, { padding: framePadding(panelRef.current), duration: 0 });
          }, 150);
        });
        observer.observe(container);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("Mapa indisponível neste navegador:", err);
        setStatus("failed");
      });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      clearTimeout(resizeTimer);
      observer?.disconnect();
      popupRef.current?.remove();
      popupRef.current = null;
      map?.remove();
      mapRef.current = null;
    };
  }, [focus]);

  // Pino ativo em destaque (e por cima do outro)
  useEffect(() => {
    mapRef.current?.markers.forEach((el, k) => {
      el.classList.toggle("is-active", k === active);
      el.style.zIndex = k === active ? "2" : "1";
    });
  }, [active, status]);

  // ?u=0|1: foca o escritório quando o mapa fica pronto e a cada clique em "Ver no mapa" (cada navegação gera uma
  // location.key nova, mesmo repetindo o link)
  useEffect(() => {
    if (status === "loading") return;
    const u = new URLSearchParams(location.search).get("u");
    const index = Number(u);
    if (u === null || !latest.current.offices[index]) return;
    const timer = window.setTimeout(() => focus(index, true), status === "ready" ? 300 : 0);
    return () => clearTimeout(timer);
  }, [location.key, location.search, status, focus]);

  const selectFromCard = (i: number) => (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("a")) return;
    focus(i, true);
  };

  return (
    <div className="cz-page cz-scope cz-map-page">
      <PageSeo page="mapa" path={MAP_PATH} />
      <Header />
      <main>
        <TitleBand
          title={
            <>
              {t("map_page.title_lead", "Onde")}{" "}
              <span className="cz-map-accent">{t("map_page.title_accent", "estamos")}</span>
            </>
          }
          icon={MapPin}
          singleLine
        />

        <section className="cz-map" aria-labelledby="cz-map-title">
          <div className="cz-map__box" id="mapa">
            <div ref={canvasRef} className="cz-map__canvas" />
            <div className="cz-map__vignette" aria-hidden="true" />
            {status === "failed" && (
              <p className="cz-map__off">
                {t("map_page.unavailable", "Mapa indisponível neste navegador. Use “Como chegar” em cada escritório.")}
              </p>
            )}

            <aside ref={panelRef} className="cz-map__panel">
              <span className="cz-eyebrow">{t("offices.title", "Escritórios")}</span>
              <h2 className="cz-map__title" id="cz-map-title">
                {t("map_page.heading", "Presença no Brasil e nos EUA")}
              </h2>
              <p className="cz-map__summary">{t("map_page.summary", "2 escritórios · São Paulo · Orlando")}</p>

              <ul className="cz-map__list">
                {offices.map((office, i) => (
                  <li
                    key={office.key}
                    className={`cz-map-office${active === i ? " is-active" : ""}`}
                    onClick={selectFromCard(i)}
                  >
                    <button type="button" className="cz-map-office__select" aria-pressed={active === i}>
                      <span className="cz-map-office__pin" aria-hidden="true">
                        <span className="cz-map-pin">
                          <b>{pad(i)}</b>
                        </span>
                      </span>
                      <span className="cz-map-office__head">
                        <span className="cz-map-office__tag">{office.tag}</span>
                        <span className="cz-map-office__city">{office.city}</span>
                      </span>
                    </button>

                    <div className="cz-map-office__info">
                      <address className="cz-map-office__addr">
                        {office.street}
                        <br />
                        {office.postal}
                      </address>
                      <div className="cz-map-office__actions">
                        <a href={office.phone.href} className="cz-map-office__phone">
                          {office.phone.label}
                        </a>
                        <a
                          href={directionsUrl(office)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cz-outline-btn cz-outline-btn--sm"
                        >
                          {t("map_page.directions", "Como chegar")}
                          <ArrowUpRight size={14} aria-hidden="true" />
                        </a>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              {status !== "failed" && (
                <button
                  type="button"
                  className="cz-outline-btn cz-outline-btn--sm cz-map__all"
                  onClick={showAll}
                  disabled={active < 0}
                >
                  <Maximize2 size={14} aria-hidden="true" />
                  {t("map_page.show_all", "Ver todos no mapa")}
                </button>
              )}
            </aside>
          </div>
        </section>

        <ContactForm />
      </main>
      <ChatWidget />
    </div>
  );
}
